import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';

export function EmailVerificationForm() {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!token) {
      setError('Invalid verification link');
      setIsVerifying(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        await api.verifyEmail(token);
        setIsVerified(true);
        toast({
          title: "Email Verified",
          description: "Your email has been successfully verified. You can now log in.",
        });
      } catch (error: any) {
        setError(error.response?.data?.error || 'Email verification failed');
        toast({
          title: "Verification Failed",
          description: error.response?.data?.error || "Email verification failed",
          variant: "destructive",
        });
      } finally {
        setIsVerifying(false);
      }
    };

    verifyEmail();
  }, [token, toast]);

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <CardTitle className="text-red-600">Invalid Link</CardTitle>
            <CardDescription>
              The verification link is invalid or malformed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/login')} 
              className="w-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ISMS Manager</h1>
          <p className="text-gray-600">Information Security Management System</p>
          <p className="text-sm text-gray-500 mt-1">NIS2 Compliance Tool</p>
        </div>

        <Card className="w-full max-w-md mx-auto">
          <CardHeader className="text-center">
            {isVerifying && (
              <>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <CardTitle>Verifying Email</CardTitle>
                <CardDescription>
                  Please wait while we verify your email address...
                </CardDescription>
              </>
            )}

            {!isVerifying && isVerified && (
              <>
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <CardTitle className="text-green-600">Email Verified!</CardTitle>
                <CardDescription>
                  Your email has been successfully verified. You can now log in to your account.
                </CardDescription>
              </>
            )}

            {!isVerifying && error && (
              <>
                <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <CardTitle className="text-red-600">Verification Failed</CardTitle>
                <CardDescription>
                  {error}
                </CardDescription>
              </>
            )}
          </CardHeader>
          
          {!isVerifying && (
            <CardContent>
              <div className="space-y-4">
                {isVerified && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <p className="text-sm text-green-800">
                      ✓ Your account is now active<br />
                      ✓ You can log in with your credentials<br />
                      ✓ Welcome to ISMS Manager!
                    </p>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-sm text-red-800">
                      The verification link may be:
                    </p>
                    <ul className="text-sm text-red-700 mt-2 list-disc list-inside">
                      <li>Expired (links are valid for 24 hours)</li>
                      <li>Already used</li>
                      <li>Invalid or corrupted</li>
                    </ul>
                  </div>
                )}

                <Button 
                  onClick={() => navigate('/login')} 
                  className="w-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {isVerified ? 'Continue to Login' : 'Back to Login'}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-xs text-gray-500">
          <p>&copy; 2025 ISMS Manager. All rights reserved.</p>
          <p className="mt-1">Built for NIS2 directive compliance</p>
        </div>
      </div>
    </div>
  );
}
